/**
 * AI-OPS-001: operator console for the assistants («یارها»).
 *
 *   /internal/admin/ai/pricing — FX, uplift, commercial policy, model token
 *     cost, publish and sync. Every price shown comes from the server; this
 *     page never computes a customer charge itself.
 *   /internal/admin/ai/usage — runs, tokens, revenue, provider cost and margin
 *     across companies, plus per-company stop/resume and promotional credit.
 *   /internal/admin/ai/finance — ADR-018 financial view: cash sales, recognized
 *     usage revenue, provider cost (surcharge included, frozen per run), margins,
 *     unused paid credit and per-request usage percentiles.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Select,
  Textarea,
} from '@sakhtar/design-system/react';
import {
  getAiFinance,
  getAiPricing,
  getAiUsage,
  getAiQuality,
  getTenantAiModels,
  setTenantAiModels,
  getAiRunDetail,
  grantAiTenantCredit,
  operatorAiErrorText,
  publishAiPricing,
  recordAiFx,
  refreshAiFx,
  saveAiPolicy,
  setAiMarkup,
  setAiModelRate,
  setAiTenantKillSwitch,
  syncAiTenants,
  type AiExampleCost,
  type AiFinanceReport,
  type AiQualityReport,
  type AiUsageMetric,
  type AiPriceLine,
  type AiPricingOverview,
  type AiSyncSummary,
  type AiTenantUsage,
  type AiUsageReport,
  type TenantModelConfig,
  type AiRunDetail,
} from '../../lib/operatorAiApi.js';
import { displayJalaliDate, displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import { OperatorPage } from './OperatorShell.js';
import { OrgAvatar, OrgUserAvatar, WithAvatar } from './OperatorAvatars.js';

const METER_LABEL: Readonly<Record<string, string>> = {
  input_token: 'توکن ورودی',
  cached_input_token: 'توکن ورودی کش‌شده',
  output_token: 'توکن خروجی',
  tool_call: 'کارمزد هر ابزار',
};

const TOOL_LABEL: Readonly<Record<string, string>> = {
  'inventory.stock.summary': 'انبار یار',
  'crm.opportunity.open': 'فروش یار',
  'procurement.order.open': 'تدارکات یار',
  'accounting.open-items': 'مالی یار',
  'projects.task.overdue': 'پروژه یار',
  'sakhtar.composite': 'ساختار یار',
};

const STATUS_LABEL: Readonly<
  Record<string, { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }>
> = {
  settled: { label: 'تسویه‌شده', tone: 'success' },
  released: { label: 'آزاد/بی‌هزینه', tone: 'default' },
  reserved: { label: 'رزرو', tone: 'info' },
  usage_unknown: { label: 'مصرف نامعلوم', tone: 'danger' },
};

function toman(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${n.toLocaleString('fa-IR', { maximumFractionDigits: 2 })} تومان`;
}

function num(value: string | number): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('fa-IR') : String(value);
}

/** "0.2" → 200000 micro-USD; exact, up to six decimals. */
export function usdToMicro(text: string): string | null {
  const trimmed = text.trim();
  const match = /^(\d{1,7})(?:\.(\d{1,6}))?$/.exec(trimmed);
  if (match === null) return null;
  const whole = BigInt(match[1] ?? '0');
  const frac = BigInt((match[2] ?? '').padEnd(6, '0') || '0');
  return (whole * 1_000_000n + frac).toString();
}

function microToUsd(micro: string): string {
  const value = BigInt(micro);
  const whole = value / 1_000_000n;
  const frac = (value % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  return frac === '' ? whole.toString() : `${whole.toString()}.${frac}`;
}

function Grid({ children, min = 220 }: { readonly children: ReactNode; readonly min?: number }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

function Stat({
  title,
  value,
  sub,
  tone,
}: {
  readonly title: string;
  readonly value: string;
  readonly sub?: string | undefined;
  readonly tone?: 'good' | 'bad';
}) {
  return (
    <Card title={title}>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          marginTop: 4,
          color: tone === 'good' ? '#059669' : tone === 'bad' ? '#dc2626' : undefined,
        }}
      >
        {value}
      </div>
      {sub ? (
        <div
          style={{ fontSize: 12, color: 'var(--sk-color-text-secondary, #6b7280)', marginTop: 4 }}
        >
          {sub}
        </div>
      ) : null}
    </Card>
  );
}

function PriceSheet({
  lines,
  example,
}: {
  readonly lines: readonly AiPriceLine[];
  readonly example: AiExampleCost;
}) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <DataTable
        columns={[
          {
            key: 'meter',
            header: 'شمارنده',
            render: (row: AiPriceLine) => METER_LABEL[row.meterCode] ?? row.meterCode,
          },
          {
            key: 'per',
            header: 'به ازای',
            render: (row: AiPriceLine) =>
              row.perUnits === '1' ? 'هر اجرا/ابزار' : `${num(row.perUnits)} توکن`,
          },
          {
            key: 'cost',
            header: 'قیمت دلاری ارائه‌دهنده (بدون کارمزد)',
            render: (row: AiPriceLine) => toman(row.costToman),
          },
          {
            key: 'price',
            header: 'قیمت مشتری',
            render: (row: AiPriceLine) => toman(row.customerToman),
          },
        ]}
        rows={[...lines]}
        rowKey={(row) => row.meterCode}
      />
      <Alert
        tone={Number(example.grossMarginToman) > 0 ? 'success' : 'danger'}
        title="نمونهٔ یک پرسش معمولی"
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <div>
            {num(example.inputTokens)} توکن ورودی + {num(example.outputTokens)} توکن خروجی +{' '}
            {num(example.toolCalls)} خواندن داده
          </div>
          <div>
            مشتری {toman(example.customerToman)} = توکن {toman(example.tokenCustomerToman)} + کارمزد
            خواندن داده {toman(example.runFeeCustomerToman)}
          </div>
          <div>
            هزینهٔ واقعی ارائه‌دهنده {toman(example.providerCostToman)} = قیمت دلاری{' '}
            {toman(example.providerListCostToman)} + کارمزد {num(example.providerFeeBps / 100)}٪{' '}
            {toman(example.providerFeeToman)} (دلار {toman(example.costTomanPerUsd)})
          </div>
          <div>
            سود ناخالص {toman(example.grossMarginToman)} ({num(example.marginPercent)}٪ از فروش)؛
            حاشیهٔ خودِ توکن بدون کارمزد خواندن: {num(example.tokenMarginPercent)}٪
          </div>
        </div>
      </Alert>
    </div>
  );
}

function useAction() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  async function run(
    fn: () => Promise<unknown>,
    success: string,
    fallback: string,
    after?: () => void,
  ) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      setMessage({ tone: 'success', text: success });
      after?.();
    } catch (error) {
      setMessage({ tone: 'danger', text: operatorAiErrorText(error, fallback) });
    } finally {
      setBusy(false);
    }
  }
  return { busy, message, run };
}

function ActionMessage({
  message,
}: {
  readonly message: { tone: 'success' | 'danger'; text: string } | null;
}) {
  if (message === null) return null;
  return (
    <Alert tone={message.tone} title={message.tone === 'success' ? 'انجام شد' : 'انجام نشد'}>
      {message.text}
    </Alert>
  );
}

// ------------------------------------------------------------------ pricing

export function AiPricingPage() {
  const [data, setData] = useState<AiPricingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sync, setSync] = useState<AiSyncSummary | null>(null);

  async function load() {
    setError(null);
    try {
      setData(await getAiPricing());
    } catch (failure) {
      setError(operatorAiErrorText(failure, 'دریافت وضعیت قیمت‌گذاری انجام نشد.'));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <OperatorPage
      title="قیمت‌گذاری یارها (هوش مصنوعی)"
      actions={
        <Button variant="secondary" onClick={() => void load()}>
          به‌روزرسانی
        </Button>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}
      {data === null && error === null ? <LoadingState /> : null}
      {data ? (
        <>
          {data.issues.length > 0 ? (
            <Alert tone="warning" title="موارد نیازمند اقدام">
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {data.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </Alert>
          ) : (
            <Alert tone="success" title="آماده">
              قیمت منتشرشده با ورودی‌های فعلی یکی است و ارائه‌دهنده پیکربندی شده است.
            </Alert>
          )}

          <Card title="فرمول قیمت مشتری">
            <p style={{ margin: 0, lineHeight: 2 }}>
              قیمت هر توکن = هزینهٔ دلاری ارائه‌دهنده × نرخ دلار × (۱ + حاشیهٔ امن نرخ ارز) × ضریب
              سود، گرد به بالا. کارمزد ثابت هر اجرا به ازای هر ابزار اضافه می‌شود. پاسخ ناموفق
              ارائه‌دهنده هزینه‌ای برای مشتری ندارد؛ پس حاشیهٔ سود باید آن را هم پوشش دهد.
            </p>
          </Card>

          <Grid min={320}>
            <Card
              title={
                data.publication
                  ? `قیمت منتشرشده (نسخهٔ ${num(data.publication.publicationVersion)})`
                  : 'قیمت منتشرشده'
              }
            >
              {data.publication ? (
                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 13 }}>
                    مدل <code>{data.publication.modelId}</code> · دلار{' '}
                    {toman(data.publication.tomanPerUsd)} · حاشیهٔ ارز{' '}
                    {num(data.publication.fxBufferBps / 100)}٪ · ضریب سود{' '}
                    {num(
                      Number(data.publication.markupNumerator) /
                        Number(data.publication.markupDenominator),
                    )}
                    × · کارمزد اجرا {toman(data.publication.runFeeToman)} · سقف خروجی{' '}
                    {num(data.publication.reserveOutputTokens)} توکن
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sk-color-text-secondary, #6b7280)' }}>
                    {displayJalaliDateTime(data.publication.effectiveFrom)} —{' '}
                    {data.publication.reason}
                  </div>
                  <PriceSheet
                    lines={data.publication.priceSheet}
                    example={data.publication.example}
                  />
                </div>
              ) : (
                <EmptyState
                  title="هنوز منتشر نشده"
                  description="پس از ثبت ورودی‌ها، قیمت را منتشر کنید."
                />
              )}
            </Card>
            <Card
              title={
                data.publicationOutdated
                  ? 'پیش‌نمایش قیمت با ورودی‌های جدید'
                  : 'پیش‌نمایش ورودی‌های فعلی'
              }
            >
              {data.preview ? (
                <PriceSheet lines={data.preview.priceSheet} example={data.preview.example} />
              ) : (
                <EmptyState
                  title="ورودی ناقص"
                  description="سیاست، مدل، ضریب سود و نرخ دلار لازم است."
                />
              )}
            </Card>
          </Grid>

          <PublishCard
            onDone={(summary) => {
              setSync(summary);
              void load();
            }}
          />
          {sync ? <SyncResult summary={sync} /> : null}

          <Grid min={320}>
            <FxCard data={data} onDone={() => void load()} />
            <MarkupCard data={data} onDone={() => void load()} />
          </Grid>
          <PolicyCard data={data} onDone={() => void load()} />
          <ModelRateCard data={data} onDone={() => void load()} />
          <ProviderRuntimeCard data={data} />
        </>
      ) : null}
    </OperatorPage>
  );
}

function SyncResult({ summary }: { readonly summary: AiSyncSummary }) {
  return (
    <Alert tone={summary.failed.length === 0 ? 'success' : 'warning'} title="همگام‌سازی شرکت‌ها">
      نسخهٔ {summary.publicationVersion ?? '—'}: {num(summary.processed)} شرکت بررسی شد،{' '}
      {num(summary.applied)} به‌روز شد، {num(summary.current)} از قبل به‌روز بود
      {summary.failed.length > 0 ? `، ${num(summary.failed.length)} ناموفق: ` : '.'}
      {summary.failed.map((f) => `${f.slug} (${f.error})`).join('، ')}
    </Alert>
  );
}

function PublishCard({ onDone }: { readonly onDone: (summary: AiSyncSummary) => void }) {
  const [reason, setReason] = useState('');
  const { busy, message, run } = useAction();
  return (
    <Card title="انتشار قیمت و همگام‌سازی">
      <div style={{ display: 'grid', gap: 12 }}>
        <FormField label="دلیل انتشار" hint="مثلاً: به‌روزرسانی نرخ دلار">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            disabled={busy || reason.trim().length < 3}
            onClick={() =>
              void run(
                async () => {
                  const result = await publishAiPricing(reason.trim());
                  onDone(result.sync);
                },
                'قیمت جدید منتشر شد و برای شرکت‌ها اعمال شد.',
                'انتشار انجام نشد.',
                () => setReason(''),
              )
            }
          >
            انتشار قیمت جدید
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() =>
              void run(
                async () => onDone(await syncAiTenants()),
                'همگام‌سازی انجام شد.',
                'همگام‌سازی انجام نشد.',
              )
            }
          >
            همگام‌سازی دوبارهٔ همهٔ شرکت‌ها
          </Button>
        </div>
        <ActionMessage message={message} />
      </div>
    </Card>
  );
}

function FxCard({
  data,
  onDone,
}: {
  readonly data: AiPricingOverview;
  readonly onDone: () => void;
}) {
  const [value, setValue] = useState('');
  const { busy, message, run } = useAction();
  return (
    <Card title="نرخ دلار (تومان)">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          فعلی:{' '}
          {data.fx
            ? `${toman(data.fx.tomanPerUsd)} — ${num(data.fx.ageHours)} ساعت پیش (${data.fx.sourceUrl === 'operator:manual' ? 'دستی' : 'خودکار'})`
            : 'ثبت نشده'}
        </div>
        <FormField label="نرخ جدید (تومان به ازای یک دلار)">
          <Input
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            disabled={busy || value === ''}
            onClick={() =>
              void run(
                () => recordAiFx(value),
                'نرخ ثبت شد. برای اعمال، قیمت را منتشر کنید.',
                'ثبت نرخ انجام نشد.',
                () => {
                  setValue('');
                  onDone();
                },
              )
            }
          >
            ثبت نرخ
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() =>
              void run(
                () => refreshAiFx(),
                'نرخ از منبع دریافت شد.',
                'دریافت خودکار نرخ انجام نشد.',
                onDone,
              )
            }
          >
            دریافت خودکار
          </Button>
        </div>
        <ActionMessage message={message} />
      </div>
    </Card>
  );
}

function MarkupCard({
  data,
  onDone,
}: {
  readonly data: AiPricingOverview;
  readonly onDone: () => void;
}) {
  const current =
    data.markup === null
      ? null
      : Math.round((Number(data.markup.numerator) / Number(data.markup.denominator) - 1) * 100);
  const [percent, setPercent] = useState('');
  const [reason, setReason] = useState('');
  const { busy, message, run } = useAction();
  const valid = /^[0-9]{1,4}$/.test(percent) && Number(percent) > 0 && Number(percent) <= 1900;
  return (
    <Card title="ضریب سود روی هزینه">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          فعلی:{' '}
          {current === null
            ? 'ثبت نشده'
            : `${num(current)}٪ سود روی هزینه (${num(Number(data.markup?.numerator) / Number(data.markup?.denominator))}×)`}
        </div>
        <FormField label="درصد سود روی هزینهٔ ارائه‌دهنده" hint="۱۰۰ یعنی قیمت فروش دو برابر هزینه">
          <Input
            inputMode="numeric"
            value={percent}
            onChange={(e) => setPercent(e.target.value.replace(/[^0-9]/g, ''))}
          />
        </FormField>
        <FormField label="دلیل">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
        <Button
          disabled={busy || !valid || reason.trim().length < 3}
          onClick={() =>
            void run(
              () => setAiMarkup(String(100 + Number(percent)), '100', reason.trim()),
              'ضریب سود ثبت شد. برای اعمال، قیمت را منتشر کنید.',
              'ثبت ضریب سود انجام نشد.',
              () => {
                setPercent('');
                setReason('');
                onDone();
              },
            )
          }
        >
          ثبت ضریب سود
        </Button>
        <ActionMessage message={message} />
      </div>
    </Card>
  );
}

function PolicyCard({
  data,
  onDone,
}: {
  readonly data: AiPricingOverview;
  readonly onDone: () => void;
}) {
  const policy = data.policy;
  const [form, setForm] = useState({
    fxBufferPercent: policy ? String(policy.fxBufferBps / 100) : '5',
    runFeeToman: policy?.runFeeToman ?? '50',
    peg: policy?.defaultMicroCreditsPerToman ?? '1000',
    reserveOutputTokens: String(policy?.reserveOutputTokens ?? 400),
    fxMaxAgeHours: String(policy?.fxMaxAgeHours ?? 72),
    allowTrialTenants: policy?.allowTrialTenants ?? true,
    platformEnabled: policy?.platformEnabled ?? true,
    providerFeePercent: String((policy?.providerFeeBps ?? 2500) / 100),
    providerTomanPerUsd: policy?.providerTomanPerUsd ?? '',
    minMarginPercent: String((policy?.minMarginBps ?? 0) / 100),
    reason: '',
  });
  const { busy, message, run } = useAction();
  const bps = Math.round(Number(form.fxBufferPercent) * 100);
  const feeBps = Math.round(Number(form.providerFeePercent) * 100);
  const marginBps = Math.round(Number(form.minMarginPercent) * 100);
  const set = (key: keyof typeof form) => (value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));
  return (
    <Card title={policy ? `سیاست تجاری (نسخهٔ ${num(policy.policyVersion)})` : 'سیاست تجاری'}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Grid>
          <FormField label="حاشیهٔ امن نرخ ارز (٪)" hint="برای جبران نوسان دلار تا انتشار بعدی">
            <Input
              inputMode="decimal"
              value={form.fxBufferPercent}
              onChange={(e) => set('fxBufferPercent')(e.target.value)}
            />
          </FormField>
          <FormField label="کارمزد ثابت هر اجرا (تومان)" hint="به ازای هر ابزار خوانده‌شده">
            <Input
              inputMode="numeric"
              value={form.runFeeToman}
              onChange={(e) => set('runFeeToman')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField
            label="میکرواعتبار به ازای هر تومان"
            hint="فقط برای شرکت‌های بدون نرخ؛ نرخ شرکت‌های موجود تغییر نمی‌کند"
          >
            <Input
              inputMode="numeric"
              value={form.peg}
              onChange={(e) => set('peg')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField label="سقف توکن خروجی هر پاسخ">
            <Input
              inputMode="numeric"
              value={form.reserveOutputTokens}
              onChange={(e) => set('reserveOutputTokens')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField label="حداکثر عمر نرخ دلار برای انتشار (ساعت)">
            <Input
              inputMode="numeric"
              value={form.fxMaxAgeHours}
              onChange={(e) => set('fxMaxAgeHours')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField
            label="کارمزد ارائه‌دهنده روی قیمت دلاری (٪)"
            hint="لیارا ۲۵٪؛ فقط در هزینه حساب می‌شود و قیمت فروش را تغییر نمی‌دهد"
          >
            <Input
              inputMode="decimal"
              value={form.providerFeePercent}
              onChange={(e) => set('providerFeePercent')(e.target.value)}
            />
          </FormField>
          <FormField
            label="نرخ دلار صورت‌حساب ارائه‌دهنده (تومان)"
            hint="خالی = نرخ بازار مبنای هزینه است"
          >
            <Input
              inputMode="numeric"
              value={form.providerTomanPerUsd}
              onChange={(e) => set('providerTomanPerUsd')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField
            label="کف حاشیهٔ سود توکن (٪ از قیمت)"
            hint="انتشار قیمتی که حاشیهٔ توکنش کمتر باشد پذیرفته نمی‌شود"
          >
            <Input
              inputMode="decimal"
              value={form.minMarginPercent}
              onChange={(e) => set('minMarginPercent')(e.target.value)}
            />
          </FormField>
        </Grid>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={form.allowTrialTenants}
            onChange={(e) => set('allowTrialTenants')(e.target.checked)}
          />
          شرکت‌های دورهٔ آزمایشی هم با اعتبار خود از یارها استفاده کنند
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={form.platformEnabled}
            onChange={(e) => set('platformEnabled')(e.target.checked)}
          />
          سرویس یارها برای همهٔ شرکت‌ها فعال باشد (خاموش = توقف کامل)
        </label>
        <FormField label="دلیل تغییر">
          <Input value={form.reason} onChange={(e) => set('reason')(e.target.value)} />
        </FormField>
        <Button
          disabled={
            busy ||
            form.reason.trim().length < 3 ||
            !Number.isFinite(bps) ||
            !Number.isFinite(feeBps) ||
            !Number.isFinite(marginBps)
          }
          onClick={() =>
            void run(
              () =>
                saveAiPolicy({
                  fxBufferBps: bps,
                  runFeeToman: form.runFeeToman || '0',
                  defaultMicroCreditsPerToman: form.peg || '0',
                  reserveOutputTokens: Number(form.reserveOutputTokens),
                  fxMaxAgeHours: Number(form.fxMaxAgeHours),
                  allowTrialTenants: form.allowTrialTenants,
                  platformEnabled: form.platformEnabled,
                  providerFeeBps: feeBps,
                  providerTomanPerUsd:
                    form.providerTomanPerUsd === '' ? null : form.providerTomanPerUsd,
                  minMarginBps: marginBps,
                  reason: form.reason.trim(),
                }),
              'سیاست ذخیره شد. توقف/دسترسی آزمایشی فوراً اعمال می‌شود؛ تغییر قیمت پس از انتشار.',
              'ذخیرهٔ سیاست انجام نشد.',
              () => {
                set('reason')('');
                onDone();
              },
            )
          }
        >
          ذخیرهٔ سیاست
        </Button>
        <ActionMessage message={message} />
      </div>
    </Card>
  );
}

function ModelRateCard({
  data,
  onDone,
}: {
  readonly data: AiPricingOverview;
  readonly onDone: () => void;
}) {
  const setting = data.providerSetting;
  const meter = (code: string) =>
    data.observedRate?.meters.find((m) => m.meterCode === code)?.microUsdPerMillion ?? null;
  const [form, setForm] = useState({
    providerCode: setting?.providerCode ?? 'liara',
    modelId: setting?.chatModelId ?? '',
    maxContextTokens: String(setting?.maxContextTokens ?? 4096),
    maxOutputTokens: String(setting?.maxOutputTokens ?? 512),
    input: meter('input_token') ? microToUsd(meter('input_token')!) : '',
    cached: meter('cached_input_token') ? microToUsd(meter('cached_input_token')!) : '',
    output: meter('output_token') ? microToUsd(meter('output_token')!) : '',
    sourceLabel: 'Liara panel',
  });
  const { busy, message, run } = useAction();
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));
  const inputMicro = usdToMicro(form.input);
  const cachedMicro = usdToMicro(form.cached === '' ? form.input : form.cached);
  const outputMicro = usdToMicro(form.output);
  return (
    <Card title="مدل پیش‌فرض و هزینهٔ توکن ارائه‌دهنده">
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          فعلی:{' '}
          {setting
            ? `${setting.providerCode} / ${setting.chatModelId} (نسخهٔ ${num(setting.settingVersion)})`
            : 'ثبت نشده'}
          {data.observedRate
            ? ` — ورودی $${microToUsd(meter('input_token') ?? '0')}، خروجی $${microToUsd(meter('output_token') ?? '0')} برای هر یک میلیون توکن`
            : ''}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>الگوهای آماده:</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setForm((f) => ({
                ...f,
                providerCode: 'liara',
                modelId: 'google/gemini-3.6-flash',
                maxContextTokens: '1000000',
                maxOutputTokens: '8192',
                input: '0.75',
                cached: '0.075',
                output: '3.75',
                sourceLabel: 'Liara AI (Gemini Flash)',
              }));
            }}
          >
            ⚡ Gemini 3.6 Flash (لیارا)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setForm((f) => ({
                ...f,
                providerCode: 'openai',
                modelId: 'openai/gpt-4o-mini',
                maxContextTokens: '128000',
                maxOutputTokens: '16384',
                input: '0.15',
                cached: '0.075',
                output: '0.60',
                sourceLabel: 'OpenAI Direct',
              }));
            }}
          >
            🤖 GPT-4o Mini
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setForm((f) => ({
                ...f,
                providerCode: 'anthropic',
                modelId: 'anthropic/claude-3-5-sonnet',
                maxContextTokens: '200000',
                maxOutputTokens: '8192',
                input: '3.00',
                cached: '0.30',
                output: '15.00',
                sourceLabel: 'Anthropic Direct',
              }));
            }}
          >
            🧠 Claude 3.5 Sonnet
          </Button>
        </div>
        <Grid>
          <FormField label="کد ارائه‌دهنده">
            <Input
              value={form.providerCode}
              onChange={(e) => set('providerCode')(e.target.value)}
            />
          </FormField>
          <FormField label="شناسهٔ مدل" hint="دقیقاً همان مدل مجاز در AI_PROVIDER_CONFIG">
            <Input value={form.modelId} onChange={(e) => set('modelId')(e.target.value)} />
          </FormField>
          <FormField label="سقف کانتکست (توکن)">
            <Input
              inputMode="numeric"
              value={form.maxContextTokens}
              onChange={(e) => set('maxContextTokens')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField label="سقف خروجی مدل (توکن)">
            <Input
              inputMode="numeric"
              value={form.maxOutputTokens}
              onChange={(e) => set('maxOutputTokens')(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </FormField>
          <FormField
            label="دلار هر ۱M توکن ورودی"
            {...(form.input !== '' && inputMicro === null ? { error: 'نامعتبر' } : {})}
          >
            <Input
              inputMode="decimal"
              value={form.input}
              onChange={(e) => set('input')(e.target.value)}
            />
          </FormField>
          <FormField label="دلار هر ۱M توکن ورودی کش‌شده" hint="خالی = برابر ورودی">
            <Input
              inputMode="decimal"
              value={form.cached}
              onChange={(e) => set('cached')(e.target.value)}
            />
          </FormField>
          <FormField
            label="دلار هر ۱M توکن خروجی"
            {...(form.output !== '' && outputMicro === null ? { error: 'نامعتبر' } : {})}
          >
            <Input
              inputMode="decimal"
              value={form.output}
              onChange={(e) => set('output')(e.target.value)}
            />
          </FormField>
          <FormField label="منبع نرخ">
            <Input value={form.sourceLabel} onChange={(e) => set('sourceLabel')(e.target.value)} />
          </FormField>
        </Grid>
        <Button
          disabled={
            busy ||
            inputMicro === null ||
            outputMicro === null ||
            cachedMicro === null ||
            form.modelId.trim() === ''
          }
          onClick={() =>
            void run(
              () =>
                setAiModelRate({
                  providerCode: form.providerCode.trim(),
                  modelId: form.modelId.trim(),
                  maxContextTokens: Number(form.maxContextTokens),
                  maxOutputTokens: Number(form.maxOutputTokens),
                  inputMicroUsdPerMillion: inputMicro ?? '0',
                  cachedInputMicroUsdPerMillion: cachedMicro ?? '0',
                  outputMicroUsdPerMillion: outputMicro ?? '0',
                  sourceLabel: form.sourceLabel.trim(),
                }),
              'مدل و نرخ توکن ثبت شد. برای اعمال، قیمت را منتشر کنید.',
              'ثبت مدل/نرخ انجام نشد.',
              onDone,
            )
          }
        >
          ثبت مدل و نرخ توکن
        </Button>
        <ActionMessage message={message} />
      </div>
    </Card>
  );
}

function ProviderRuntimeCard({ data }: { readonly data: AiPricingOverview }) {
  const runtime = data.providerRuntime;
  return (
    <Card title="اتصال ارائه‌دهنده روی سرور">
      <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
        <div>
          <Badge tone={runtime.configured ? 'success' : 'danger'}>
            {runtime.configured ? 'AI_PROVIDER_CONFIG تنظیم شده' : 'AI_PROVIDER_CONFIG تنظیم نشده'}
          </Badge>{' '}
          {runtime.killSwitch || runtime.envKillSwitch ? (
            <Badge tone="danger">کلید توقف سرور فعال</Badge>
          ) : null}
        </div>
        {runtime.endpoints.map((endpoint) => (
          <div key={endpoint.providerCode}>
            <Badge tone={endpoint.enabled ? 'success' : 'default'}>{endpoint.providerCode}</Badge>{' '}
            مدل‌ها: {endpoint.models.join('، ') || '—'}
          </div>
        ))}
        <div style={{ color: 'var(--sk-color-text-secondary, #6b7280)' }}>
          کلید API و نشانی سرویس فقط در متغیرهای محیطی سرور نگه‌داری می‌شوند و اینجا نمایش داده
          نمی‌شوند.
        </div>
      </div>
    </Card>
  );
}

// -------------------------------------------------------------------- usage

export function AiUsagePage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AiUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modelModalSlug, setModelModalSlug] = useState<string | null>(null);
  const [inspectRun, setInspectRun] = useState<{ slug: string; runId: string } | null>(null);
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [toolFilter, setToolFilter] = useState<string>('all');
  const [searchFilter, setSearchFilter] = useState<string>('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setData(await getAiUsage(days));
    } catch (failure) {
      setError(operatorAiErrorText(failure, 'دریافت گزارش مصرف انجام نشد.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [days]);

  const totals = data?.totals;
  const filteredRuns = (data?.recentRuns ?? []).filter((run) => {
    if (tenantFilter !== 'all' && run.slug !== tenantFilter) return false;
    if (toolFilter !== 'all' && run.tool !== toolFilter && run.assistantKey !== toolFilter)
      return false;
    if (searchFilter.trim().length > 0) {
      const q = searchFilter.toLowerCase();
      const matchQ = run.question?.toLowerCase().includes(q);
      const matchA = run.answer?.toLowerCase().includes(q);
      const matchUser = run.actorName?.toLowerCase().includes(q);
      const matchModel = run.modelCode?.toLowerCase().includes(q);
      if (!matchQ && !matchA && !matchUser && !matchModel) return false;
    }
    return true;
  });
  return (
    <OperatorPage
      title="پایش مصرف و سود یارها"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="بازهٔ گزارش"
          >
            <option value="1">۲۴ ساعت</option>
            <option value="7">۷ روز</option>
            <option value="30">۳۰ روز</option>
            <option value="90">۹۰ روز</option>
          </Select>
          <Button variant="secondary" disabled={loading} onClick={() => void load()}>
            {loading ? 'در حال دریافت…' : 'به‌روزرسانی'}
          </Button>
        </div>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}
      {loading && data === null ? <LoadingState /> : null}
      {data && totals ? (
        <>
          {totals.unknownUsageRuns > 0 ? (
            <Alert tone="danger" title="اجراهای با مصرف نامعلوم">
              {num(totals.unknownUsageRuns)} اجرا مصرف ارائه‌دهنده را گزارش نکرده‌اند و هنوز از
              مشتری کسر نشده‌اند.
            </Alert>
          ) : null}
          <Grid>
            <Stat
              title="درآمد (کسر از کیف پول)"
              value={toman(totals.revenueToman)}
              sub={`${num(totals.settledRuns)} اجرای تسویه‌شده`}
            />
            <Stat
              title="هزینهٔ ارائه‌دهنده"
              value={toman(totals.providerCostToman)}
              sub="با کارمزد ارائه‌دهنده، به نرخ ثبت‌شدهٔ هر اجرا"
            />
            <Stat
              title="سود ناخالص"
              value={toman(totals.grossMarginToman)}
              sub={`${num(totals.marginPercent)}٪ از درآمد`}
              tone={Number(totals.grossMarginToman) >= 0 ? 'good' : 'bad'}
            />
            <Stat title="شارژ کیف پول (پرداخت موفق)" value={toman(totals.purchasesToman)} />
            <Stat
              title="مانده کیف پول شرکت‌ها"
              value={toman(totals.outstandingBalanceToman)}
              sub="تعهد خدمت به مشتری"
            />
            <Stat title="اعتبار هدیهٔ داده‌شده (کل)" value={toman(totals.welcomeToman)} />
            <Stat
              title="اجراها"
              value={num(totals.runs)}
              sub={`ناموفق/آزاد ${num(totals.failedRuns)} · در جریان ${num(totals.pendingRuns)} · نامعلوم ${num(totals.unknownUsageRuns)}`}
            />
            <Stat
              title="توکن‌ها"
              value={`${num(totals.inputTokens)} / ${num(totals.outputTokens)}`}
              sub="ورودی / خروجی"
            />
            <Stat
              title="شرکت‌ها"
              value={`${num(totals.optedInTenants)} از ${num(totals.tenants)}`}
              sub="دستیار فعال‌شده"
            />
          </Grid>

          <Card title="روند روزانه">
            {data.daily.length === 0 ? (
              <EmptyState
                title="اجرایی در این بازه نیست"
                description="هنوز پرسشی از یارها پرسیده نشده است."
              />
            ) : (
              <DailyBars daily={data.daily} />
            )}
          </Card>

          <Card title="به تفکیک یار">
            <DataTable
              columns={[
                {
                  key: 'tool',
                  header: 'یار',
                  render: (row: AiUsageReport['byTool'][number]) =>
                    TOOL_LABEL[row.tool] ?? row.tool,
                },
                {
                  key: 'runs',
                  header: 'اجرا',
                  render: (row: AiUsageReport['byTool'][number]) =>
                    `${num(row.runs)} (${num(row.settled)} تسویه)`,
                },
                {
                  key: 'rev',
                  header: 'درآمد',
                  render: (row: AiUsageReport['byTool'][number]) => toman(row.revenueToman),
                },
                {
                  key: 'cost',
                  header: 'هزینه',
                  render: (row: AiUsageReport['byTool'][number]) => toman(row.providerCostToman),
                },
                {
                  key: 'margin',
                  header: 'سود',
                  render: (row: AiUsageReport['byTool'][number]) => toman(row.grossMarginToman),
                },
              ]}
              rows={[...data.byTool]}
              rowKey={(row) => row.tool}
              empty={<EmptyState title="داده‌ای نیست" description="هنوز اجرایی ثبت نشده است." />}
            />
          </Card>

          <Card title="به تفکیک شرکت">
            <DataTable
              columns={[
                {
                  key: 'org',
                  header: 'شرکت',
                  render: (row: AiTenantUsage) => (
                    <WithAvatar avatar={<OrgAvatar slug={row.slug} name={row.displayName} />}>
                      <div style={{ fontWeight: 600 }}>{row.displayName}</div>
                      <Link
                        to={`/internal/admin/organizations/${encodeURIComponent(row.slug)}`}
                        style={{ fontSize: 12, fontFamily: 'monospace' }}
                      >
                        {row.slug}
                      </Link>
                    </WithAvatar>
                  ),
                },
                {
                  key: 'state',
                  header: 'وضعیت',
                  render: (row: AiTenantUsage) => (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Badge tone={row.optedIn ? 'success' : 'default'}>
                        {row.optedIn ? 'فعال‌شده' : 'غیرفعال'}
                      </Badge>
                      {row.killSwitch ? <Badge tone="danger">متوقف</Badge> : null}
                      {row.microCreditsPerToman === null ? (
                        <Badge tone="warning">بدون قیمت</Badge>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'balance',
                  header: 'مانده',
                  render: (row: AiTenantUsage) => toman(row.balanceToman),
                },
                {
                  key: 'runs',
                  header: 'اجرا',
                  render: (row: AiTenantUsage) => `${num(row.runs)} / ${num(row.settledRuns)}`,
                },
                {
                  key: 'rev',
                  header: 'درآمد',
                  render: (row: AiTenantUsage) => toman(row.revenueToman),
                },
                {
                  key: 'cost',
                  header: 'هزینه',
                  render: (row: AiTenantUsage) => toman(row.providerCostToman),
                },
                {
                  key: 'margin',
                  header: 'سود',
                  render: (row: AiTenantUsage) => toman(row.grossMarginToman),
                },
                {
                  key: 'buy',
                  header: 'خرید اعتبار',
                  render: (row: AiTenantUsage) => toman(row.purchasesToman),
                },
                {
                  key: 'last',
                  header: 'آخرین اجرا',
                  render: (row: AiTenantUsage) =>
                    row.lastRunAt ? displayJalaliDateTime(row.lastRunAt) : '—',
                },
                {
                  key: 'actions',
                  header: 'اقدام',
                  render: (row: AiTenantUsage) => (
                    <TenantActions
                      row={row}
                      onDone={() => void load()}
                      onConfigureModels={(slug) => setModelModalSlug(slug)}
                    />
                  ),
                },
              ]}
              rows={[...data.byTenant]}
              rowKey={(row) => row.slug}
              empty={<EmptyState title="شرکتی نیست" description="شرکت فعالی پیدا نشد." />}
            />
          </Card>

          <Card title="پایش لاگ و دیالوگ‌های هوش مصنوعی تننت‌ها">
            {/* Filter toolbar */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                marginBottom: '16px',
                alignItems: 'center',
              }}
            >
              <Input
                placeholder="جستجو در متن سوال، پاسخ، کاربر یا مدل…"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ minWidth: 260, flex: 1 }}
              />
              <Select
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                style={{ width: '160px' }}
                aria-label="فیلتر شرکت"
              >
                <option value="all">همهٔ شرکت‌ها</option>
                {data.byTenant.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.displayName || t.slug}
                  </option>
                ))}
              </Select>
              <Select
                value={toolFilter}
                onChange={(e) => setToolFilter(e.target.value)}
                style={{ width: '150px' }}
                aria-label="فیلتر یار"
              >
                <option value="all">همهٔ یارها</option>
                <option value="inventory.stock.summary">انبار یار</option>
                <option value="crm.opportunity.open">فروش یار</option>
                <option value="accounting.open-items">مالی یار</option>
                <option value="procurement.order.open">تدارکات یار</option>
                <option value="projects.task.overdue">پروژه یار</option>
                <option value="sakhtar.composite">ساختار یار</option>
              </Select>
              {tenantFilter !== 'all' || toolFilter !== 'all' || searchFilter ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setTenantFilter('all');
                    setToolFilter('all');
                    setSearchFilter('');
                  }}
                >
                  پاک‌سازی فیلتر
                </Button>
              ) : null}
            </div>

            <DataTable
              columns={[
                {
                  key: 'time',
                  header: 'زمان',
                  render: (row: AiUsageReport['recentRuns'][number]) =>
                    displayJalaliDateTime(row.createdAt),
                },
                {
                  key: 'org',
                  header: 'شرکت و کاربر',
                  render: (row: AiUsageReport['recentRuns'][number]) => (
                    <WithAvatar avatar={<OrgAvatar slug={row.slug} name={row.slug} size={30} />}>
                      <strong style={{ fontSize: '13px' }}>{row.slug}</strong>
                      {row.actorName ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: '11px',
                            color: '#64748b',
                            marginTop: 2,
                          }}
                        >
                          <OrgUserAvatar
                            slug={row.slug}
                            accountId={row.actorId}
                            name={row.actorName}
                            size={20}
                          />
                          {row.actorName}
                        </div>
                      ) : null}
                    </WithAvatar>
                  ),
                },
                {
                  key: 'tool',
                  header: 'یار / ماژول',
                  render: (row: AiUsageReport['recentRuns'][number]) => (
                    <Badge tone="info">{TOOL_LABEL[row.tool] ?? row.tool}</Badge>
                  ),
                },
                {
                  key: 'question',
                  header: 'پرسش کاربر',
                  render: (row: AiUsageReport['recentRuns'][number]) => (
                    <div
                      style={{
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        color: row.question ? '#1e293b' : '#94a3b8',
                      }}
                      title={row.question ?? undefined}
                    >
                      {row.question || '—'}
                    </div>
                  ),
                },
                {
                  key: 'model',
                  header: 'مدل',
                  render: (row: AiUsageReport['recentRuns'][number]) =>
                    row.modelCode ? (
                      <Badge tone="info">{row.modelCode}</Badge>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '11px' }}>—</span>
                    ),
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (row: AiUsageReport['recentRuns'][number]) => {
                    const status = STATUS_LABEL[row.billingStatus] ?? {
                      label: row.billingStatus,
                      tone: 'default' as const,
                    };
                    return <Badge tone={status.tone}>{status.label}</Badge>;
                  },
                },
                {
                  key: 'tokens',
                  header: 'توکن و هزینه',
                  render: (row: AiUsageReport['recentRuns'][number]) => (
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {row.inputTokens === null ? '—' : num(row.inputTokens)} /{' '}
                        {row.outputTokens === null ? '—' : num(row.outputTokens)}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '12px' }}>
                        {toman(row.chargeToman)}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'action',
                  header: 'دیالوگ و جزییات',
                  render: (row: AiUsageReport['recentRuns'][number]) => (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setInspectRun({ slug: row.slug, runId: row.runId })}
                    >
                      بررسی دیالوگ
                    </Button>
                  ),
                },
              ]}
              rows={[...filteredRuns]}
              rowKey={(row) => `${row.slug}:${row.runId}`}
              empty={
                <EmptyState
                  title="اجرایی پیدا نشد"
                  description={
                    searchFilter || tenantFilter !== 'all' || toolFilter !== 'all'
                      ? 'هیچ پرسشی با این فیلترها مطابقت ندارد.'
                      : 'در این بازه پرسشی اجرا نشده است.'
                  }
                />
              }
            />
          </Card>

          {data.failures.length > 0 ? (
            <Alert tone="warning" title="شرکت‌هایی که خوانده نشدند">
              {data.failures.map((f) => `${f.slug}: ${f.error}`).join('، ')}
            </Alert>
          ) : null}

          {modelModalSlug ? (
            <TenantModelModal
              slug={modelModalSlug}
              onClose={() => setModelModalSlug(null)}
              onSaved={() => void load()}
            />
          ) : null}

          {inspectRun ? (
            <RunDetailModal
              slug={inspectRun.slug}
              runId={inspectRun.runId}
              onClose={() => setInspectRun(null)}
            />
          ) : null}
        </>
      ) : null}
    </OperatorPage>
  );
}

function DailyBars({ daily }: { readonly daily: AiUsageReport['daily'] }) {
  const max = Math.max(
    1,
    ...daily.map((d) => Math.max(Number(d.revenueToman), Number(d.providerCostToman))),
  );
  return (
    <div style={{ display: 'grid', gap: 6 }} role="table" aria-label="درآمد و هزینهٔ روزانه">
      {daily.map((day) => (
        <div
          key={day.day}
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: '88px 1fr',
            gap: 8,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <span role="cell" style={{ fontFamily: 'monospace' }}>
            {day.day}
          </span>
          <div role="cell" style={{ display: 'grid', gap: 2 }}>
            <div
              title={`درآمد ${toman(day.revenueToman)}`}
              style={{
                height: 8,
                borderRadius: 4,
                background: '#10b981',
                width: `${Math.max(1, (Number(day.revenueToman) / max) * 100)}%`,
              }}
            />
            <div
              title={`هزینه ${toman(day.providerCostToman)}`}
              style={{
                height: 8,
                borderRadius: 4,
                background: '#f59e0b',
                width: `${Math.max(1, (Number(day.providerCostToman) / max) * 100)}%`,
              }}
            />
            <span style={{ color: 'var(--sk-color-text-secondary, #6b7280)' }}>
              {num(day.runs)} اجرا · درآمد {toman(day.revenueToman)} · هزینه{' '}
              {toman(day.providerCostToman)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TenantActions({
  row,
  onDone,
  onConfigureModels,
}: {
  readonly row: AiTenantUsage;
  readonly onDone: () => void;
  readonly onConfigureModels: (slug: string) => void;
}) {
  const [mode, setMode] = useState<'none' | 'grant' | 'kill'>('none');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const { busy, message, run } = useAction();
  if (mode === 'none') {
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={() => onConfigureModels(row.slug)}>
          سوییچ مدل
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setMode('grant')}>
          هدیهٔ اعتبار
        </Button>
        <Button
          size="sm"
          variant={row.killSwitch ? 'success' : 'danger'}
          onClick={() => setMode('kill')}
        >
          {row.killSwitch ? 'ازسرگیری' : 'توقف'}
        </Button>
        <ActionMessage message={message} />
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 200 }}>
      {mode === 'grant' ? (
        <Input
          placeholder="مبلغ (تومان)"
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
        />
      ) : null}
      <Input placeholder="دلیل" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div style={{ display: 'flex', gap: 4 }}>
        <Button
          size="sm"
          disabled={busy || reason.trim().length < 3 || (mode === 'grant' && amount === '')}
          onClick={() =>
            void run(
              () =>
                mode === 'grant'
                  ? grantAiTenantCredit(row.slug, amount, reason.trim())
                  : setAiTenantKillSwitch(row.slug, !row.killSwitch, reason.trim()),
              mode === 'grant' ? 'اعتبار هدیه ثبت شد.' : 'وضعیت دستیار شرکت تغییر کرد.',
              'انجام نشد.',
              () => {
                setMode('none');
                setAmount('');
                setReason('');
                onDone();
              },
            )
          }
        >
          تأیید
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setMode('none')}>
          انصراف
        </Button>
      </div>
      <ActionMessage message={message} />
    </div>
  );
}

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(3px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '16px',
};

const modalContentStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  maxWidth: '740px',
  width: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  direction: 'rtl',
};

function TenantModelModal({
  slug,
  onClose,
  onSaved,
}: {
  readonly slug: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<TenantModelConfig | null>(null);
  const [activeTab, setActiveTab] = useState<'models' | 'prompts'>('models');
  const [selectedYarKey, setSelectedYarKey] = useState<string>('finance');
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [yarModels, setYarModels] = useState<Record<string, string>>({});
  const [yarPrompts, setYarPrompts] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<string>('تنظیم مدل‌ها و پرامپت‌های اختصاصی یارها');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getTenantAiModels(slug)
      .then((res) => {
        if (!active) return;
        setConfig(res);
        setDefaultModel(res.defaultModelCode ?? 'google/gemini-3.6-flash');
        setYarModels(res.yarModels ?? {});
        setYarPrompts(res.yarPrompts ?? {});
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(operatorAiErrorText(err, 'دریافت تنظیمات مدل تننت انجام نشد.'));
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await setTenantAiModels(slug, {
        defaultModelCode: defaultModel || null,
        yarModels,
        yarPrompts,
        reason: reason.trim(),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(operatorAiErrorText(err, 'ذخیره تنظیمات مدل و پرامپت انجام نشد.'));
    } finally {
      setSaving(false);
    }
  }

  const YAR_DOMAINS: readonly { key: string; label: string; icon: string }[] = [
    { key: 'finance', label: 'مالی یار (حسابداری و تراز)', icon: '💰' },
    { key: 'sales', label: 'فروش یار (فرصت‌ها و پیش‌فاکتور)', icon: '📈' },
    { key: 'inventory', label: 'انبار یار (موجودی و کاردکس)', icon: '📦' },
    { key: 'procurement', label: 'تدارکات یار (سفارش‌های خرید)', icon: '🛒' },
    { key: 'projects', label: 'پروژه یار (وظایف و پیگیری‌ها)', icon: '⏱️' },
    { key: 'sakhtar', label: 'ساختار یار (عمومی و مرکب)', icon: '🏢' },
  ];

  const customPromptsCount = Object.values(yarPrompts).filter(
    (p) => p && p.trim().length > 0,
  ).length;

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={{ ...modalContentStyle, maxWidth: '820px' }} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
              سوییچ مدل و تنظیم پرامپت اختصاصی یارها
            </h3>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: 2 }}>
              شرکت: {config?.displayName ?? slug} ({slug})
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            background: '#f8fafc',
            padding: '0 20px',
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('models')}
            style={{
              padding: '12px 18px',
              fontSize: '13px',
              fontWeight: activeTab === 'models' ? 700 : 500,
              color: activeTab === 'models' ? '#0284c7' : '#64748b',
              borderBottom: activeTab === 'models' ? '2px solid #0284c7' : '2px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🤖</span>
            <span>مدل‌های هوش مصنوعی</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('prompts')}
            style={{
              padding: '12px 18px',
              fontSize: '13px',
              fontWeight: activeTab === 'prompts' ? 700 : 500,
              color: activeTab === 'prompts' ? '#0284c7' : '#64748b',
              borderBottom: activeTab === 'prompts' ? '2px solid #0284c7' : '2px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>✍️</span>
            <span>شخصی‌سازی پرامپت تک‌تک یارها</span>
            {customPromptsCount > 0 ? (
              <span
                style={{
                  background: '#0284c7',
                  color: '#ffffff',
                  fontSize: '10px',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  fontWeight: 700,
                }}
              >
                {customPromptsCount.toLocaleString('fa-IR')}
              </span>
            ) : null}
          </button>
        </div>

        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {error ? (
            <Alert tone="danger" title="خطا">
              {error}
            </Alert>
          ) : null}
          {loading ? <LoadingState /> : null}

          {config && activeTab === 'models' ? (
            <>
              <div
                style={{
                  background: '#f8fafc',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 600,
                    marginBottom: '6px',
                  }}
                >
                  مدل پیش‌فرض هوش مصنوعی تننت:
                </label>
                <Select
                  value={defaultModel}
                  onChange={(e) => setDefaultModel(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {config.availableModels.map((m) => (
                    <option key={m.modelCode} value={m.modelCode}>
                      {m.displayName} ({m.providerCode}) — رده {m.tier} —{' '}
                      {m.maxContextTokens.toLocaleString('fa-IR')} توکن
                    </option>
                  ))}
                </Select>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  این مدل برای تمام پرسش‌هایی که یار مربوطه مدل اختصاصی ندارد اعمال می‌شود.
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 600 }}>
                  سوییچ مدل به تفکیک یارهای تخصصی (Per-Yar Overrides):
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '12px',
                  }}
                >
                  {YAR_DOMAINS.map((yar) => (
                    <div
                      key={yar.key}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        padding: '12px',
                        borderRadius: '8px',
                      }}
                    >
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 600,
                          marginBottom: '4px',
                        }}
                      >
                        {yar.icon} {yar.label}:
                      </label>
                      <Select
                        value={yarModels[yar.key] ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setYarModels((prev) => {
                            const next = { ...prev };
                            if (!val) {
                              delete next[yar.key];
                            } else {
                              next[yar.key] = val;
                            }
                            return next;
                          });
                        }}
                        style={{ width: '100%', fontSize: '12px' }}
                      >
                        <option value="">(استفاده از مدل پیش‌فرض تننت)</option>
                        {config.availableModels.map((m) => (
                          <option key={m.modelCode} value={m.modelCode}>
                            {m.displayName} ({m.providerCode})
                          </option>
                        ))}
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {config && activeTab === 'prompts' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
                می‌توانید برای هر یک از یارهای شش‌گانه، قواعد، لحن یا اولویت‌های سفارشی سازمان را
                وارد کنید. این متن در ابتدای پرامپت تحلیلی یار قرار می‌گیرد و به عنوان دستورالعمل
                سیستمی معتبر اعمال می‌شود.
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
                  gap: '8px',
                }}
              >
                {YAR_DOMAINS.map((yar) => {
                  const hasCustom = Boolean(yarPrompts[yar.key]?.trim());
                  const isSelected = selectedYarKey === yar.key;
                  return (
                    <button
                      key={yar.key}
                      type="button"
                      onClick={() => setSelectedYarKey(yar.key)}
                      style={{
                        padding: '10px 8px',
                        borderRadius: '8px',
                        border: isSelected ? '2px solid #0284c7' : '1px solid #e2e8f0',
                        background: isSelected ? '#f0f9ff' : '#ffffff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{yar.icon}</span>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? '#0369a1' : '#334155',
                        }}
                      >
                        {yar.label.split(' ')[0]}
                      </span>
                      <Badge
                        tone={hasCustom ? 'info' : 'default'}
                        style={{ fontSize: '10px', padding: '1px 5px' }}
                      >
                        {hasCustom ? 'سفارشی' : 'پیش‌فرض'}
                      </Badge>
                    </button>
                  );
                })}
              </div>

              {(() => {
                const activeYar =
                  YAR_DOMAINS.find((y) => y.key === selectedYarKey) ?? YAR_DOMAINS[0]!;
                const defaultInfo = config.defaultPrompts?.[selectedYarKey];
                const currentCustomPrompt = yarPrompts[selectedYarKey] ?? '';

                return (
                  <div
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                          {activeYar.icon} {activeYar.label}
                        </span>
                        {defaultInfo?.role ? (
                          <span style={{ fontSize: '12px', color: '#64748b', marginRight: '8px' }}>
                            ({defaultInfo.role})
                          </span>
                        ) : null}
                      </div>
                      {currentCustomPrompt.trim() ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setYarPrompts((prev) => {
                              const next = { ...prev };
                              delete next[selectedYarKey];
                              return next;
                            });
                          }}
                        >
                          🔄 بازنشانی به پیش‌فرض سیستم
                        </Button>
                      ) : null}
                    </div>

                    {defaultInfo?.rules ? (
                      <div
                        style={{
                          background: '#f8fafc',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0',
                          fontSize: '12px',
                          color: '#475569',
                          lineHeight: 1.6,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '2px',
                            color: '#334155',
                          }}
                        >
                          📌 دستورالعمل پایه و پیش‌فرض موتور هوش مصنوعی:
                        </span>
                        {defaultInfo.rules}
                      </div>
                    ) : null}

                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 600,
                          marginBottom: '6px',
                          color: '#1e293b',
                        }}
                      >
                        دستورالعمل و پرامپت اختصاصی این سازمان برای {activeYar.label.split(' ')[0]}:
                      </label>
                      <Textarea
                        value={currentCustomPrompt}
                        onChange={(e) => {
                          const val = e.target.value;
                          setYarPrompts((prev) => ({
                            ...prev,
                            [selectedYarKey]: val,
                          }));
                        }}
                        rows={5}
                        placeholder={`دستورالعمل‌ها و قواعد اختصاصی را وارد کنید...\nمثال: در تحلیل‌های مالی، مطالبات بالای ۳۰ روز مشتریان گروه ویژه را مشخص کن و ریسک اعتباری را با دقت بسنج.`}
                        style={{ width: '100%', fontSize: '12px', lineHeight: 1.6 }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '4px',
                          fontSize: '11px',
                          color: '#64748b',
                        }}
                      >
                        <span>
                          {currentCustomPrompt.trim()
                            ? '✅ پرامپت اختصاصی فعال است و به عنوان دستورالعمل معتبر اعمال می‌شود.'
                            : 'ℹ️ پرامپت خالی است؛ رفتار استاندارد سیستم اجرا خواهد شد.'}
                        </span>
                        <span>{currentCustomPrompt.length.toLocaleString('fa-IR')} کاراکتر</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : null}

          {config ? (
            <div>
              <label
                style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}
              >
                دلیل تغییرات (جهت ثبت در لاگ امنیتی اپراتور):
              </label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثلاً: ارتقای دقت مالی یار و تنظیم پرامپت اختصاصی سازمان"
                style={{ width: '100%' }}
              />
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={saving || loading || reason.trim().length < 3}
          >
            {saving ? 'در حال ذخیره…' : 'ذخیره و اعمال تنظیمات'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RunDetailModal({
  slug,
  runId,
  onClose,
}: {
  readonly slug: string;
  readonly runId: string;
  readonly onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<AiRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getAiRunDetail(slug, runId)
      .then((res) => {
        if (!active) return;
        setDetail(res);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(operatorAiErrorText(err, 'دریافت جزئیات اجرا انجام نشد.'));
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug, runId]);

  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
              بررسی کامل دیالوگ و استعلام یار
            </h3>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: 2 }}>
              شناسه اجرا: <code style={{ fontSize: '11px' }}>{runId}</code>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {error ? (
            <Alert tone="danger" title="خطا">
              {error}
            </Alert>
          ) : null}
          {loading ? <LoadingState /> : null}

          {detail ? (
            <>
              {/* Meta strip */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              >
                <div>
                  <strong>سازمان:</strong> {detail.tenantName} ({detail.slug})
                </div>
                <div>
                  <strong>یار:</strong>{' '}
                  <Badge tone="info">{TOOL_LABEL[detail.tool ?? ''] ?? detail.tool}</Badge>
                </div>
                <div>
                  <strong>کاربر:</strong>{' '}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <OrgUserAvatar
                      slug={detail.slug}
                      accountId={detail.actorId}
                      name={detail.actorName || 'ک'}
                      size={22}
                    />
                    {detail.actorName || detail.actorId || 'نامشخص'}
                  </span>
                </div>
                <div>
                  <strong>مدل:</strong> <Badge tone="info">{detail.modelCode ?? 'نامشخص'}</Badge>
                </div>
                <div>
                  <strong>زمان:</strong> {displayJalaliDateTime(detail.createdAt)}
                </div>
              </div>

              {/* Question */}
              <div
                style={{
                  background: '#f8fafc',
                  padding: '14px',
                  borderRadius: '8px',
                  borderRight: '4px solid #3b82f6',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#2563eb',
                    marginBottom: '6px',
                  }}
                >
                  پرسش مطرح‌شده توسط کاربر:
                </div>
                <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#1e293b' }}>
                  {detail.question || 'استعلام مستقیم ساختار بدون متن پرامپت'}
                </div>
              </div>

              {/* Answer */}
              <div
                style={{
                  background: '#f0fdf4',
                  padding: '14px',
                  borderRadius: '8px',
                  borderRight: '4px solid #10b981',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#059669',
                    marginBottom: '6px',
                  }}
                >
                  پاسخ تولیدشده توسط یار ({detail.modelCode}):
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    lineHeight: 1.7,
                    color: '#0f172a',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {detail.answer || 'پاسخی در دیتابیس ثبت نشده است.'}
                </div>
              </div>

              {/* Citations */}
              {detail.citations && detail.citations.length > 0 ? (
                <div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#475569',
                      marginBottom: '6px',
                    }}
                  >
                    مستندات و آیین‌نامه‌های استنادشده:
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {detail.citations.map((c, idx) => (
                      <Badge key={idx} tone="info">
                        📄 {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* ERP Evidence summary */}
              {detail.evidence && detail.evidence.length > 0 ? (
                <details
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px 14px',
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#475569',
                    }}
                  >
                    مشاهده {detail.evidence.length} ردیف داده و شواهد رابطه‌ای استخراج‌شده از ERP
                  </summary>
                  <pre
                    style={{
                      margin: '10px 0 0 0',
                      maxHeight: '180px',
                      overflowY: 'auto',
                      fontSize: '11px',
                      direction: 'ltr',
                      background: '#ffffff',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    {JSON.stringify(detail.evidence, null, 2)}
                  </pre>
                </details>
              ) : null}

              {/* Usage & Cost stats */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                  gap: '10px',
                  padding: '12px',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                }}
              >
                <div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>توکن ورودی</div>
                  <strong>{num(detail.tokens.inputTokens)}</strong>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>توکن خروجی</div>
                  <strong>{num(detail.tokens.outputTokens)}</strong>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>کسر از اعتبار مشتری</div>
                  <strong style={{ color: '#16a34a' }}>{toman(detail.chargeToman)}</strong>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>هزینه ارائه‌دهنده</div>
                  <strong>{toman(detail.providerCostToman)}</strong>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <Button variant="secondary" onClick={onClose}>
            بستن
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ finance

const METRIC_ROWS: readonly {
  readonly key: keyof Pick<
    AiFinanceReport['metrics'],
    | 'inputTokens'
    | 'cachedInputTokens'
    | 'outputTokens'
    | 'reasoningTokens'
    | 'weightedTokens'
    | 'providerCostToman'
    | 'chargeToman'
  >;
  readonly label: string;
  readonly money?: boolean;
}[] = [
  { key: 'inputTokens', label: 'توکن ورودی' },
  { key: 'cachedInputTokens', label: 'توکن ورودی کش‌شده' },
  { key: 'outputTokens', label: 'توکن خروجی (با استدلال)' },
  { key: 'reasoningTokens', label: 'توکن استدلال' },
  { key: 'weightedTokens', label: 'توکن معادل (وزن‌دار)' },
  { key: 'providerCostToman', label: 'هزینهٔ ارائه‌دهنده هر پرسش', money: true },
  { key: 'chargeToman', label: 'مبلغ دریافتی هر پرسش', money: true },
];

export function AiFinancePage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AiFinanceReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setData(await getAiFinance(days));
    } catch (failure) {
      setError(operatorAiErrorText(failure, 'دریافت گزارش مالی انجام نشد.'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [days]);

  const totals = data?.totals;
  const metrics = data?.metrics;
  const cell = (value: string | null, money?: boolean) =>
    value === null ? '—' : money ? toman(value) : num(value);
  return (
    <OperatorPage
      title="حساب سود و زیان یارها"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="بازهٔ گزارش"
          >
            <option value="7">۷ روز</option>
            <option value="30">۳۰ روز</option>
            <option value="90">۹۰ روز</option>
            <option value="365">۱۲ ماه</option>
          </Select>
          <Button variant="secondary" disabled={loading} onClick={() => void load()}>
            {loading ? 'در حال دریافت…' : 'به‌روزرسانی'}
          </Button>
        </div>
      }
    >
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}
      {loading && data === null ? <LoadingState /> : null}
      {data && totals && metrics ? (
        <>
          <Alert tone="info" title="مبنای محاسبه">
            هزینهٔ هر اجرا هنگام تسویه با نرخ دلار و کارمزد ارائه‌دهندهٔ همان زمان ثبت و قفل می‌شود؛
            تغییر نرخ‌ها نتایج گذشته را تغییر نمی‌دهد. اجراهای قبل از ثبت این اطلاعات با نرخ انتشار
            خودشان برآورد شده‌اند ({num(totals.estimatedCostRuns)} اجرا).
          </Alert>
          {totals.unknownRuns > 0 ? (
            <Alert tone="danger" title="هزینه بدون درآمد">
              {num(totals.unknownRuns)} اجرا مصرف نامعلوم دارند؛ هزینهٔ ارائه‌دهندهٔ آن‌ها (
              {toman(totals.unbilledProviderCostToman)}) از سود کم شده است.
            </Alert>
          ) : null}
          {metrics.outputCapReachedRuns > 0 ? (
            <Alert tone="warning" title="پاسخ‌هایی که به سقف خروجی رسیدند">
              {num(metrics.outputCapReachedRuns)} پاسخ از {num(metrics.measuredRuns)} به سقف توکن
              خروجی رسیده‌اند و ممکن است ناقص باشند. ذخیرهٔ خروجی را در قیمت‌گذاری افزایش دهید.
            </Alert>
          ) : null}
          <Grid>
            <Stat
              title="فروش نقدی بسته/شارژ"
              value={toman(totals.cashSalesToman)}
              sub={`${num(totals.paidOrders)} پرداخت موفق · کارمزد درگاه ${toman(totals.gatewayFeeToman)}`}
            />
            <Stat
              title="درآمد شناسایی‌شده از مصرف"
              value={toman(totals.paidUsageRevenueToman)}
              sub="مصرف از اعتبار خریداری‌شده"
            />
            <Stat
              title="مصرف اعتبار هدیه"
              value={toman(totals.promotionalUsageToman)}
              sub="بدون درآمد نقدی"
            />
            <Stat
              title="هزینهٔ ارائه‌دهنده"
              value={toman(totals.providerCostToman)}
              sub={`قیمت دلاری ${toman(totals.providerListCostToman)} + کارمزد ${toman(totals.providerFeeToman)}`}
            />
            <Stat
              title="سود ناخالص"
              value={toman(totals.grossMarginToman)}
              sub={`${num(totals.grossMarginPercent)}٪ از درآمد شناسایی‌شده`}
              tone={Number(totals.grossMarginToman) >= 0 ? 'good' : 'bad'}
            />
            <Stat
              title="بدهی اعتبار پرداخت‌شدهٔ مصرف‌نشده"
              value={toman(totals.unusedPaidCreditToman)}
              sub={`اعتبار هدیهٔ مانده ${toman(totals.unusedPromotionalCreditToman)}`}
            />
          </Grid>

          <Card title="تفکیک درآمد و حاشیه">
            <DataTable
              columns={[
                { key: 'k', header: 'شرح', render: (row: readonly [string, string]) => row[0] },
                { key: 'v', header: 'مقدار', render: (row: readonly [string, string]) => row[1] },
              ]}
              rows={[
                ['ارزش کل مصرف تسویه‌شده (نقدی + هدیه)', toman(totals.usageValueToman)],
                ['سهم توکن', toman(totals.tokenRevenueToman)],
                [
                  `کارمزد خواندن داده (${num(totals.billableReads)} خواندن)`,
                  toman(totals.runFeeRevenueToman),
                ],
                [
                  'حاشیهٔ کل مصرف (پیش از اجراهای نامعلوم)',
                  `${toman(totals.usageMarginToman)} (${num(totals.usageMarginPercent)}٪)`,
                ],
                [
                  'حاشیهٔ توکن بدون کارمزد خواندن',
                  `${toman(totals.tokenMarginToman)} (${num(totals.tokenMarginPercent)}٪)`,
                ],
                ['هزینهٔ اجراهای با مصرف نامعلوم', toman(totals.unbilledProviderCostToman)],
                [
                  'اجراها',
                  `${num(totals.runs)} · تسویه ${num(totals.settledRuns)} · آزاد ${num(totals.releasedRuns)} · در جریان ${num(totals.pendingRuns)} · نامعلوم ${num(totals.unknownRuns)}`,
                ],
              ]}
              rowKey={(row) => row[0]}
            />
          </Card>

          <Card
            title={`مصرف واقعی ساختار یار به ازای هر پرسش (${num(metrics.measuredRuns)} اجرای اندازه‌گیری‌شده)`}
          >
            {metrics.measuredRuns === 0 ? (
              <EmptyState
                title="هنوز اجرای اندازه‌گیری‌شده‌ای نیست"
                description="از این نسخه به بعد هر پرسش تسویه‌شده ثبت می‌شود."
              />
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                <DataTable
                  columns={[
                    {
                      key: 'label',
                      header: 'شاخص',
                      render: (row: (typeof METRIC_ROWS)[number]) => row.label,
                    },
                    ...(['p50', 'p90', 'p99', 'mean'] as const).map((key) => ({
                      key,
                      header: key === 'mean' ? 'میانگین' : key,
                      render: (row: (typeof METRIC_ROWS)[number]) =>
                        cell((metrics[row.key] as AiUsageMetric)[key], row.money),
                    })),
                  ]}
                  rows={[...METRIC_ROWS]}
                  rowKey={(row) => row.key}
                />
                <div style={{ fontSize: 12, color: 'var(--sk-color-text-secondary, #6b7280)' }}>
                  سهم ورودی کش‌شده {num(metrics.cachedInputPercent)}٪ · سهم استدلال از خروجی{' '}
                  {num(metrics.reasoningOfOutputPercent)}٪ ({num(metrics.reasoningReportedRuns)}{' '}
                  اجرا با گزارش استدلال). توکن معادل: ورودی ۱، کش‌شده ۰٫۱، خروجی ۶ (نسبت قیمت
                  ارائه‌دهنده).
                </div>
              </div>
            )}
          </Card>

          <Card title="به تفکیک شرکت">
            {data.byTenant.length === 0 ? (
              <EmptyState title="داده‌ای در این بازه نیست" description="" />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'name',
                    header: 'شرکت',
                    render: (row: AiFinanceReport['byTenant'][number]) => (
                      <WithAvatar avatar={<OrgAvatar slug={row.slug} name={row.displayName} />}>
                        {row.displayName}
                      </WithAvatar>
                    ),
                  },
                  {
                    key: 'cash',
                    header: 'فروش نقدی',
                    render: (row: AiFinanceReport['byTenant'][number]) => toman(row.cashSalesToman),
                  },
                  {
                    key: 'rev',
                    header: 'درآمد شناسایی‌شده',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      toman(row.paidUsageRevenueToman),
                  },
                  {
                    key: 'promo',
                    header: 'مصرف هدیه',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      toman(row.promotionalUsageToman),
                  },
                  {
                    key: 'cost',
                    header: 'هزینهٔ ارائه‌دهنده',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      toman(row.providerCostToman),
                  },
                  {
                    key: 'margin',
                    header: 'سود ناخالص',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      `${toman(row.grossMarginToman)} (${num(row.grossMarginPercent)}٪)`,
                  },
                  {
                    key: 'liability',
                    header: 'اعتبار پرداخت‌شدهٔ مانده',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      toman(row.unusedPaidCreditToman),
                  },
                  {
                    key: 'runs',
                    header: 'اجرا / خواندن',
                    render: (row: AiFinanceReport['byTenant'][number]) =>
                      `${num(row.settledRuns)} / ${num(row.billableReads)}`,
                  },
                ]}
                rows={[...data.byTenant]}
                rowKey={(row) => row.slug}
              />
            )}
          </Card>

          <Card title="روزانه">
            {data.byDay.length === 0 ? (
              <EmptyState title="داده‌ای در این بازه نیست" description="" />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'day',
                    header: 'روز',
                    render: (row: AiFinanceReport['byDay'][number]) => displayJalaliDate(row.day),
                  },
                  {
                    key: 'runs',
                    header: 'اجرا',
                    render: (row: AiFinanceReport['byDay'][number]) => num(row.runs),
                  },
                  {
                    key: 'cash',
                    header: 'فروش نقدی',
                    render: (row: AiFinanceReport['byDay'][number]) => toman(row.cashSalesToman),
                  },
                  {
                    key: 'rev',
                    header: 'درآمد شناسایی‌شده',
                    render: (row: AiFinanceReport['byDay'][number]) =>
                      toman(row.paidUsageRevenueToman),
                  },
                  {
                    key: 'cost',
                    header: 'هزینهٔ ارائه‌دهنده',
                    render: (row: AiFinanceReport['byDay'][number]) => toman(row.providerCostToman),
                  },
                  {
                    key: 'margin',
                    header: 'سود ناخالص',
                    render: (row: AiFinanceReport['byDay'][number]) => toman(row.grossMarginToman),
                  },
                ]}
                rows={[...data.byDay].reverse()}
                rowKey={(row) => row.day}
              />
            )}
          </Card>

          {data.failures.length > 0 ? (
            <Alert tone="warning" title="شرکت‌هایی که خوانده نشدند">
              {data.failures.map((failure) => `${failure.slug}: ${failure.error}`).join(' · ')}
            </Alert>
          ) : null}
        </>
      ) : null}
    </OperatorPage>
  );
}

export function AiQualityPage() {
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AiQualityReport | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAiQuality(Number(days) || 30)
      .then((report) => {
        if (!cancelled) setData(report);
      })
      .catch((err) => {
        if (!cancelled) setError(operatorAiErrorText(err, 'دریافت گزارش کیفیت و خطاها انجام نشد.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <OperatorPage
      title="کیفیت، خطاها و بازخورد هوش مصنوعی"
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Select value={days} onChange={(e) => setDays(e.target.value)} aria-label="بازه زمانی">
            <option value="7">۷ روز اخیر</option>
            <option value="30">۳۰ روز اخیر</option>
            <option value="90">۹۰ روز اخیر</option>
          </Select>
        </div>
      }
    >
      {loading ? (
        <LoadingState label="در حال محاسبه شاخص‌های کیفیت و خطاها…" />
      ) : error ? (
        <Alert tone="warning" title="خطا">
          {error}
        </Alert>
      ) : data ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 14,
              marginBlockEnd: 20,
            }}
          >
            <Card style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>کل درخواست‌ها</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>
                {num(data.totalRuns)}
              </div>
            </Card>

            <Card style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>درخواست‌های موفق</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, color: '#10b981' }}>
                {num(data.successfulRuns)}
              </div>
            </Card>

            <Card style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>نرخ خطا</div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  marginTop: 4,
                  color: data.failedRuns > 0 ? '#ef4444' : 'inherit',
                }}
              >
                {data.errorRatePercent}
              </div>
            </Card>

            <Card style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>
                پوشش استناد و مستندسازی
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, color: '#3b82f6' }}>
                {data.groundingRatePercent}
              </div>
            </Card>

            <Card style={{ padding: '16px 20px' }}>
              <div style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>رضایت کاربران</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, color: '#8b5cf6' }}>
                {data.feedbackSummary.satisfactionPercent}
              </div>
            </Card>
          </div>

          <Card title="تفکیک علل و کدهای خطا">
            {data.failuresByError.length === 0 ? (
              <EmptyState
                title="هیچ خطایی ثبت نشده است"
                description="در بازه زمانی انتخاب‌شده کلیه درخواست‌های مدل و دستیارها با موفقیت اجرا شده‌اند."
              />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'error',
                    header: 'کد و شرح خطا',
                    render: (row: { error: string; count: number }) => row.error,
                  },
                  {
                    key: 'count',
                    header: 'تعداد وقوع',
                    render: (row: { error: string; count: number }) => num(row.count),
                  },
                ]}
                rows={[...data.failuresByError]}
                rowKey={(row) => row.error}
              />
            )}
          </Card>

          <Card title="آخرین خطاهای ثبت‌شده در پلتفرم" style={{ marginTop: 20 }}>
            {data.recentFailures.length === 0 ? (
              <EmptyState
                title="هیچ اجرای ناموفقی وجود ندارد"
                description="پلتفرم در وضعیت پایدار قرار دارد."
              />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'createdAt',
                    header: 'زمان',
                    render: (row: AiQualityReport['recentFailures'][number]) =>
                      displayJalaliDateTime(row.createdAt),
                  },
                  {
                    key: 'slug',
                    header: 'سازمان',
                    render: (row: AiQualityReport['recentFailures'][number]) => row.slug,
                  },
                  {
                    key: 'tool',
                    header: 'ابزار',
                    render: (row: AiQualityReport['recentFailures'][number]) => row.tool,
                  },
                  {
                    key: 'error',
                    header: 'نوع خطا',
                    render: (row: AiQualityReport['recentFailures'][number]) => (
                      <Badge tone="danger">{row.error}</Badge>
                    ),
                  },
                  {
                    key: 'runId',
                    header: 'شناسه اجرا',
                    render: (row: AiQualityReport['recentFailures'][number]) => (
                      <span className="sk-num ltrcell">{row.runId}</span>
                    ),
                  },
                ]}
                rows={[...data.recentFailures]}
                rowKey={(row) => row.runId}
              />
            )}
          </Card>
        </>
      ) : null}
    </OperatorPage>
  );
}
